import React, { useState, useCallback } from "react";
import classNames from "classnames";
import { compose } from "lib/apolloClient";
import { connect } from "react-redux";
import { AI_GRADING_HELP_CENTER_URL } from "Constants";

// Internal imports
import { useI18n } from "Hooks";
import { openLinkInNewTab } from "modules/Services";
import { updateSettings } from "modules/ConfigurationThunks";
import { getSettingValue } from "Utils/ConfigurationUtils";

// Design System imports
import { DSProvider, IconButton, Badge, Button } from "@toddle-edu/ds-web";
import { CloseOutlined, VideosOutlined } from "@toddle-edu/ds-icons";
import { elevations } from "@toddle-edu/ds-theme";
import { ArrowContainer, Popover } from "@toddle-edu/react-tiny-popover";

// Styles
import classes from "./AssignmentEvaluationAICallout.scss";

const styles = {
  calloutContainerStyle: {
    zIndex: 1100,
    top: "4px",
  },
  arrowContainer: {
    padding: 0,
  },
  arrowStyle: {
    top: 1.5,
    borderBottom: `10px solid var(--surface-primary-enabled)`,
    zIndex: 1200,
  },
};

const AI_ASSISTED_GRADING_DEMO_VIDEO =
  "https://iframe.dacast.com/vod/ea71159a-bfba-7a06-500f-93b65dfdcdf5/dc8745df-df9e-40fa-bd64-87c324887680";

const DemoVideo = ({ videoUrl }) => {
  const { t } = useI18n(["a11y"]);
  const handleVideoContainerClick = useCallback(e => {
    e.stopPropagation();
    if (e.nativeEvent) {
      e.nativeEvent.stopImmediatePropagation();
    }
  }, []);

  return (
    <DSProvider dsVersion="1.0">
      <div
        className={classes.videoPlayerContainer}
        onClick={handleVideoContainerClick}
        tabIndex={0}
        role="button"
        aria-label="Watch AI Grading demo video"
      >
        <iframe
          src={videoUrl || AI_ASSISTED_GRADING_DEMO_VIDEO}
          width="100%"
          height="100%"
          title={t("a11y:ai_grading_demo_video")}
          allowFullScreen="true"
        />
      </div>
    </DSProvider>
  );
};

const PopoverContent = ({
  toggleCallout,
  title,
  message,
  openLinkInNewTab,
  videoUrl,
}) => {
  const { t } = useI18n(["ai"]);

  const handleLearnBestPractices = () => {
    openLinkInNewTab({ url: AI_GRADING_HELP_CENTER_URL });
    toggleCallout();
  };

  return (
    <div
      className={classes.demoVideoContentContainer}
      style={elevations["elevation-2B"]}
    >
      <div className={classNames("text-heading-6", classes.headingContainer)}>
        <h6
          className={classNames(classes.contentHeading)}
          style={{ display: "inline" }}
        >
          {title || t("ai:call_out_title")}
        </h6>
        <IconButton
          type="plain"
          size="small"
          dsVersion="2.0"
          variant="neutral"
          onClick={toggleCallout}
          icon={<CloseOutlined />}
          aria-label="Close AI callout"
        />
      </div>
      <div className={classes.demoVideoContentBody}>
        <span className="text-body">
          {message || t("ai:call_out_subtitle")}
        </span>
        <DemoVideo videoUrl={videoUrl} />
        <Button
          dsVersion="2.0"
          variant="neutral"
          type="outlined"
          isFullWidth={true}
          className={classes.learnBestPracticesButton}
          onClick={handleLearnBestPractices}
          testId="learn-best-practices-button"
        >
          {t("ai:explore_best_practices")}
        </Button>
      </div>
    </div>
  );
};

const AssignmentEvaluationAICallout = props => {
  const { title, message, updateSettings, openLinkInNewTab, videoUrl } = props;

  const [showCallout, setShowCallout] = useState(false);

  const isAIGradingCalloutSeen = getSettingValue({
    name: "isAIGradingCalloutSeen",
    readFromUserAndOrgLevel: true,
  });

  const toggleCallout = () => {
    setShowCallout(prev => !prev);
    if (!isAIGradingCalloutSeen) {
      updateSettings({
        settings: [
          {
            name: "isAIGradingCalloutSeen",
            value: "true",
          },
        ],
        showToastMsg: false,
      });
    }
  };

  return (
    <Popover
      isOpen={showCallout}
      positions={["bottom"]}
      align="end"
      boundaryInset={20}
      reposition={true}
      onClickOutside={toggleCallout}
      containerStyle={styles.calloutContainerStyle}
      containerClassName={classes.popoverContainer}
      content={({ position, childRect, popoverRect }) => (
        <ArrowContainer
          position={position}
          childRect={childRect}
          popoverRect={popoverRect}
          arrowSize={10}
          arrowColor="var(--border-secondary)"
        >
          <ArrowContainer
            position={position}
            childRect={childRect}
            popoverRect={popoverRect}
            arrowSize={10}
            style={styles.arrowContainer}
            arrowStyle={styles.arrowStyle}
          >
            <PopoverContent
              toggleCallout={toggleCallout}
              title={title}
              message={message}
              openLinkInNewTab={openLinkInNewTab}
              videoUrl={videoUrl}
            />
          </ArrowContainer>
        </ArrowContainer>
      )}
    >
      <div className={classes.aiCalloutIcon}>
        <IconButton
          variant="neutral"
          type="outlined"
          dsVersion="2.0"
          icon={<VideosOutlined />}
          testId="evaluation-ai-demo"
          onClick={toggleCallout}
          shouldStopPropagation={true}
          aria-label="View AI evaluation demo"
        />
        {!isAIGradingCalloutSeen ? (
          <div className={classes.aiCalloutBadge}>
            <Badge
              dsVersion="2.0"
              variant="active"
              type="dot"
              shouldAnimateActiveDotBadge={true}
            />
          </div>
        ) : null}
      </div>
    </Popover>
  );
};

const mapActionCreators = {
  updateSettings,
  openLinkInNewTab,
};

export default compose(connect(null, mapActionCreators))(
  AssignmentEvaluationAICallout
);
